import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import {
    PictureAsPdf as PdfIcon,
    GridOn as ExcelIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import JsdhNotifikaceService, { ReportFilterParams } from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { downloadFile } from '../../utils/downloadFile';
import { useToast } from '../toast/useToast';

interface ExportMenuProps {
    params: ReportFilterParams;
    disabled?: boolean;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ params, disabled = false }) => {
    const { showToast } = useToast();
    const [pdfAnchor, setPdfAnchor] = useState<null | HTMLElement>(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingXlsx, setLoadingXlsx] = useState(false);

    const handlePdfMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setPdfAnchor(event.currentTarget);
    };

    const handlePdfMenuClose = () => {
        setPdfAnchor(null);
    };

    const handleExportPdf = async (styl: string) => {
        handlePdfMenuClose();
        setLoadingPdf(true);
        try {
            const { blob, filename } = await JsdhNotifikaceService.exportReportPdf({ ...params, styl });
            downloadFile(blob, filename);
            showToast('Report PDF byl úspěšně stažen.', 'success');
        } catch {
            showToast('Nepodařilo se exportovat report PDF.', 'error');
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleExportXlsx = async () => {
        setLoadingXlsx(true);
        try {
            const { blob, filename } = await JsdhNotifikaceService.exportReportXlsx(params);
            downloadFile(blob, filename);
            showToast('Export Excel byl úspěšně stažen.', 'success');
        } catch {
            showToast('Nepodařilo se exportovat report Excel.', 'error');
        } finally {
            setLoadingXlsx(false);
        }
    };

    return (
        <Box display="flex" gap={1} flexShrink={0}>
            <Tooltip title={disabled ? 'Žádné výjezdy v tomto období' : ''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loadingPdf ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                        endIcon={<ExpandMoreIcon />}
                        onClick={handlePdfMenuOpen}
                        disabled={disabled || loadingPdf}
                        size="small"
                    >
                        Export PDF
                    </Button>
                </span>
            </Tooltip>

            <Menu
                anchorEl={pdfAnchor}
                open={Boolean(pdfAnchor)}
                onClose={handlePdfMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => handleExportPdf('vykaz-urad')}>
                    Výkaz pro úřad
                </MenuItem>
                <MenuItem onClick={() => handleExportPdf('vyrocni-report')}>
                    Výroční report
                </MenuItem>
            </Menu>

            <Tooltip title={disabled ? 'Žádné výjezdy v tomto období' : ''}>
                <span>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={loadingXlsx ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
                        onClick={handleExportXlsx}
                        disabled={disabled || loadingXlsx}
                        size="small"
                    >
                        Export Excel
                    </Button>
                </span>
            </Tooltip>
        </Box>
    );
};

export default ExportMenu;
